import { useBookingModuleStoreActions } from "PFApp/booking/store";
import { RefObject, useCallback } from "react";

import { PointerDownData } from "./booking_drag_and_drop_manager.types";
import { useBookingBarManager } from "./use_booking_bar_manager";
import { useBookingDropManager } from "./use_booking_drop_manager";
import { useBookingDropZoneManager } from "./use_booking_drop_zone_manager";
import { useDragAxes } from "./use_drag_axes";

type UseDragSession = {
  dropZoneRef: RefObject<HTMLDivElement | null>;
};

type UseDragSessionReturn = {
  startDrag: (pointerDownData: PointerDownData) => void;
  moveDrag: (event: PointerEvent) => void;
  cancelDrag: () => void;
  dropDrag: () => void;
};

export const useDragSession = ({ dropZoneRef }: UseDragSession): UseDragSessionReturn => {
  const { setBookingDragAndDropData, resetBookingDragAndDropData, getBookingDragAndDropData } =
    useBookingModuleStoreActions();
  const bookingBarManager = useBookingBarManager();
  const dropZoneManager = useBookingDropZoneManager({ dropZoneRef });
  const dropManager = useBookingDropManager({
    onDragEnd: bookingBarManager.onDragEnd,
    onDragCancel: bookingBarManager.onDragCancel
  });
  const { getDragAxes } = useDragAxes();

  const startDrag = useCallback(
    (pointerDownData: PointerDownData) => {
      setBookingDragAndDropData({
        draggingMode: pointerDownData.draggingMode,
        bookingId: pointerDownData.bookingId,
        draggingStartDate: null,
        draggingEndDate: null,
        isDraggingExiting: false,
        sourceRow: pointerDownData.sourceRow,
        targetRow: null
      });
      bookingBarManager.onDragStart({ originX: pointerDownData.originX, originY: pointerDownData.originY });
      dropZoneManager.onDragStart();
    },
    [bookingBarManager, dropZoneManager, setBookingDragAndDropData]
  );

  const moveDrag = useCallback(
    (event: PointerEvent) => {
      // Bar manager first: drop zone reads bar.style.left/width to position itself.
      const axes = getDragAxes();
      bookingBarManager.onDragMove(event, axes);
      dropZoneManager.onDragMove(event, axes);
    },
    [bookingBarManager, dropZoneManager, getDragAxes]
  );

  const cancelDrag = useCallback(() => {
    if (getBookingDragAndDropData().draggingMode === null) {
      return;
    }

    dropZoneManager.onDragEnd();
    bookingBarManager.onDragCancel();
    resetBookingDragAndDropData();
  }, [bookingBarManager, dropZoneManager, getBookingDragAndDropData, resetBookingDragAndDropData]);

  const dropDrag = useCallback(() => {
    const dragData = getBookingDragAndDropData();
    if (dragData.draggingMode === null) {
      return;
    }

    dropZoneManager.onDragEnd();
    setBookingDragAndDropData({ draggingMode: null, isDraggingExiting: true });
    // The click after pointerup fires before this timer, so the panel still ignores it
    setTimeout(() => setBookingDragAndDropData({ isDraggingExiting: false }), 0);

    void dropManager.onDrop(dragData);
  }, [dropManager, dropZoneManager, getBookingDragAndDropData, setBookingDragAndDropData]);

  return { startDrag, moveDrag, cancelDrag, dropDrag };
};
